/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Route from '@ember/routing/route';
import type Router from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type Folder from 'embercom/models/content-service/folder';
import type Store from '@ember-data/store';
import KnowledgeHubApi, {
  type KnowledgeHubApiQueryParams,
} from 'embercom/lib/knowledge-hub/list-api';
import { getOwner } from '@ember/application';
import type Transition from '@ember/routing/transition';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import { type SortDirection } from 'embercom/lib/knowledge-hub/constants';
import { EntityType } from 'embercom/models/data/entity-types';
import { type ContentSearchParams } from 'embercom/lib/content-service/search-api';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type FolderController from 'embercom/controllers/apps/app/knowledge-hub/folder';

type ModelParams = {
  folder_id: string;
};

export default class FolderRoute extends Route {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare store: Store;
  @service declare appService: $TSFixMe;
  @service declare router: Router;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;

  async beforeModel(transition: Transition) {
    // Only fetch if we don't already have the usage summary data
    if (!this.knowledgeHubService.usageSummary) {
      // Ensure sync sources are loaded before component renders
      // This is needed for synced folders to properly show the "manage sources" button
      await this.knowledgeHubService.fetchKnowledgeUsageSummary();
    }

    // Only proceed to super.beforeModel if we get here (no errors thrown)
    return super.beforeModel(transition);
  }

  async model(params: ModelParams): Promise<Folder> {
    return await this.getFolder(params.folder_id);
  }

  async afterModel(folder: Folder): Promise<KnowledgeHubApi> {
    if (!folder?.id) {
      this.router.transitionTo('apps.app.knowledge-hub.all-content');
    }

    let defaultSearchParams: ContentSearchParams = {
      object_types: this.knowledgeHubService.getSearchObjectTypes(),
      content_wrapper_types: [ContentWrapperType.SUPPORT_CONTENT],
      app_id: this.appService.app.id as string,
      folder_ids: [folder.id],
      folder_entity_type: EntityType.ContentLibraryFolder,
      sort_by: 'title',
      sort_direction: 'asc' as SortDirection,
    };

    let folderQueryParams = this.paramsFor(this.routeName) as KnowledgeHubApiQueryParams;

    this.knowledgeHubService.api = new KnowledgeHubApi(
      getOwner(this),
      defaultSearchParams,
      folderQueryParams,
    );
    this.knowledgeHubService.api.loadPage();
    return this.knowledgeHubService.api;
  }

  async setupController(controller: any, folder: Folder, transition: Transition) {
    super.setupController(controller, folder, transition);
    controller.setProperties({ listApi: this.knowledgeHubService.api, folder });
  }

  async getFolder(folder_id: string): Promise<Folder> {
    await this.knowledgeHubService.fetchFoldersOnce();
    return this.store.peekRecord('content-service/folder', folder_id);
  }

  resetController(controller: FolderController, isExiting: boolean) {
    if (isExiting) {
      this.knowledgeHubDrawerEditorService.removeEmberStickyQueryParams(controller);
    }
  }
}
